import appConfig from '../config/appConfig';
import * as dataConfig from '../config/dataConfig/dataConfig';
import * as localizationKeys from '../consts/localization/localizationKeys';
import ModalTypes from '../enums/app/modalTypes';
import NotificationModalTypes from '../enums/app/notificationModalTypes';
import {setConsumerRecentlyPairedDevices} from '../state/ducks/consumer';
import {setNotificationSuccess} from '../state/ducks/global';
import {makeSelectDeviceName, selectConsumerRecentlyPairedDevices} from '../state/selectors/consumer';
import {selectIsRegistrationWas, selectIsSkipRegistration} from '../state/selectors/global';
import {selectIotDeviceType} from '../state/selectors/iotDevice';
import {makeSelectVisibleGlobalModal} from '../state/selectors/modal';
import {dispatch, getState} from '../state/store';
import elementsTesIds from '../test/consts/elementsTesIds';
import analyticsService from './analyticsService';
import connectivityService from './device/connectivityService';
import deviceTypesService from './device/deviceTypesService';
import {getLocalizedStrings} from './localization/localizationService';
import appRouterService from './route/appRouterService';

const getPairingData = (isBle, deviceType) => {
    if (isBle) {
        const data = dataConfig.getPairingGuideData(deviceType);

        if (!data) return null;

        const shouldDefaultDataBeOmitted = dataConfig.getShouldDefaultDataBeOmitted();

        if (shouldDefaultDataBeOmitted) {
            return {contentList: data.content};
        } else {
            return {contentList: data.content, videoSrc: data.video, thumbnailSrc: data.thumbnail};
        }
    } else {
        const data = deviceTypesService.getDeviceTypesDataByType(deviceType);

        if (data) {
            return {
                contentList: [{isConnectionStep: true}],
                deviceName: data.name,
            };
        }
    }
};

const makeFinishRedirect = async (deviceType) => {
    const state = getState();
    const isRegWas = selectIsRegistrationWas(state);
    const isSkipRegistration = selectIsSkipRegistration(state);
    const isQuickStartGuidePageEnabled = appConfig.getIsQuickStartGuidePageEnabled();
    const isQuickStartGuideBannerEnabled = appConfig.getIsQuickStartGuideBannerEnabled(deviceType);
    const isVisibleQSGPopup = makeSelectVisibleGlobalModal(ModalTypes.QUICK_START_GUIDE_POPUP)(state);

    //show success connect page after reg if no QSG popup
    if (!isVisibleQSGPopup && (isRegWas || isSkipRegistration)) {
        dispatch(setConsumerRecentlyPairedDevices(deviceType));
        appRouterService.forwardToPairingSuccessPage();
        return;
    }

    //if QSG banner turned on then redirect to device settings
    if (isQuickStartGuideBannerEnabled) {
        showQSGSuccessConnectNotification(isVisibleQSGPopup);
        dispatch(setConsumerRecentlyPairedDevices(deviceType));
        appRouterService.forwardToDeviceSettingsPage();
        return;
    }

    const isDeviceTypeExistsInConsumerRecentlyPairedDevices =
        _isDeviceTypeExistsInConsumerRecentlyPairedDevices(deviceType);

    showSuccessConnectNotification();
    if (isDeviceTypeExistsInConsumerRecentlyPairedDevices) {
        appRouterService.forwardToDeviceSettingsPage();
    } else {
        dispatch(setConsumerRecentlyPairedDevices(deviceType));
        if (isQuickStartGuidePageEnabled) {
            appRouterService.forwardToQuickStartGuidePage();
        } else {
            appRouterService.forwardToDeviceSettingsPage();
        }
    }
};

const showSuccessConnectNotification = () => {
    const state = getState();
    const isBle = connectivityService.isBle();
    const name = makeSelectDeviceName()(state);
    const type = selectIotDeviceType(state);
    const localizedStrings = getLocalizedStrings();
    const connectMessageKey = isBle
        ? localizationKeys.BLE_DEVICE_PAIRED_SUCCESSFULLY_NOTIFICATION
        : localizationKeys.USB_DEVICE_PAIRED_SUCCESSFULLY_NOTIFICATION;
    const connectMessage = localizedStrings.formatString(localizedStrings[connectMessageKey], name);

    dispatch(setNotificationSuccess(connectMessage, elementsTesIds.PAIRING_SEQUENCE_SUCCESSFULLY_PAIRED_TEXT_NOTIFIC));
    analyticsService.pushSuccessPairingConnectingDeviceNotificationEvent(type, isBle);
};

const showQSGSuccessConnectNotification = (isVisibleQSGPopup) => {
    if (isVisibleQSGPopup) {
        dispatch(setNotificationSuccess(null, null, NotificationModalTypes.QSG_DEVICE_PAIRED_NOTIFICATION));
    } else {
        showSuccessConnectNotification();
    }
};

const _isDeviceTypeExistsInConsumerRecentlyPairedDevices = (deviceType) => {
    const consumerRecentlyPairedDevices = selectConsumerRecentlyPairedDevices(getState());

    if (consumerRecentlyPairedDevices?.length) {
        return consumerRecentlyPairedDevices?.some((type) => type === deviceType);
    }
};

const getPairingButtonProp = ({usbProp, bleProp, commonProp, cancelProp, deviceType}) => {
    const isBle = connectivityService.isBle();
    const isBleAndUsbSupported =
        connectivityService.isBleAndUsbSupported() && !deviceTypesService.isDeviceBleDisabledByType(deviceType);

    if (isBleAndUsbSupported) {
        return isBle
            ? {
                  mainButtonProp: bleProp,
                  secondaryButtonProp: usbProp,
                  tertiaryButtonProp: cancelProp,
              }
            : {
                  mainButtonProp: usbProp,
                  secondaryButtonProp: bleProp,
                  tertiaryButtonProp: cancelProp,
              };
    } else {
        return {
            mainButtonProp: commonProp,
            secondaryButtonProp: cancelProp,
            tertiaryButtonProp: {},
        };
    }
};

export default {
    getPairingData,
    makeFinishRedirect,
    getPairingButtonProp,
};
